import { motion } from "motion/react";
import { Link } from "react-router";
import { Check, ArrowRight, Plus } from "lucide-react";

export function Preise() {
  const packageFeatures = [
    "Professionelles Design",
    "Hosting inklusive",
    "Responsive für alle Geräte",
    "Regelmäßige Updates",
    "SSL-Verschlüsselung",
    "Support inklusive",
    "DSGVO-konform",
    "Barrierefreiheit",
  ];

  const addons = [
    {
      name: "Einzelanpassungen",
      price: "80 € pro Stunde",
      description: "Für dringenden Mehraufwand",
    },
    {
      name: "Zusätzliche Seiten",
      price: "160 € pro Seite",
      description: "Mehr als 5 Seiten gewünscht? Kein Problem.",
    },
    {
      name: "Content-Erstellung",
      price: "200 € pro Monat",
      description: "Für Content-Marketing und aktuelle Beiträge für bis zu 3 Beiträge",
    },
    {
      name: "SEO-Paket Premium",
      price: "300 € einmalig",
      description: "Erweiterte SEO-Optimierung und Recherche für bis zu 3 Keywords",
    },
    {
      name: "Buchungssystem",
      price: "400 € einmalig",
      description: "Online-Terminbuchung / Reservierungen",
    },
  ];

  const included = [
    "Bis zu 5 Seiten",
    "Responsive Design",
    "Hosting & Domain-Verwaltung",
    "SSL-Verschlüsselung",
    "DSGVO-konform",
    "Barrierefreiheit WCAG 2.2",
    "Kontaktformular",
    "Google Maps Integration",
    "Cookie-Banner",
    "SEO-Grundoptimierung",
    "Regelmäßige Updates",
    "Sicherheits-Monitoring",
    "Performance-Optimierung",
    "Backups",
    "Support per E-Mail",
    "99 % Uptime-Garantie",
  ];

  const faq = [
    {
      question: "Gibt es versteckte Kosten?",
      answer:
        "Nein. 500 € Setup + 99 € monatlich, jeweils zzgl. gesetzlicher MwSt. Nur Add-ons kosten extra – und die sind transparent aufgeführt.",
    },
    {
      question: "Kann ich monatlich kündigen?",
      answer:
        "Die Mindestlaufzeit beträgt 12 Monate. Danach ist eine monatliche Kündigung in Textform, aktuell insbesondere per E-Mail, möglich.",
    },
    {
      question: "Können monatliche Supportstunden angespart werden?",
      answer:
        "Nein. Die monatlichen Supportstunden gelten jeweils nur für den laufenden Monat und können nicht in Folgemonate übertragen oder angespart werden.",
    },
    {
      question: "Was passiert mit meiner Website, wenn ich kündige?",
      answer:
        "Der Betrieb durch uns endet zum Vertragsende. Eine automatische Export- oder Handover-Funktion gibt es derzeit nicht; eine etwaige Übergabe von Dateien oder Zugängen wird gesondert abgestimmt.",
    },
    {
      question: "Kann ich nachträglich Funktionen hinzufügen?",
      answer:
        "Ja, jederzeit. Add-ons können Sie auch nach dem Launch buchen.",
    },
    {
      question: "Was passiert, wenn ich bereits einen Webserver und eine Domain habe?",
      answer:
        "In diesem Fall bekommen Sie einen Rabatt auf den Monatsbeitrag in Höhe von 10 €.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Website Package */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl lg:text-5xl mb-6">Unser Website-Paket</h1>
            <p className="text-xl text-gray-600">
              Professionell umgesetzt, individuell zugeschnitten, langfristig betreut
            </p>
            <p className="mt-4 text-sm font-medium text-gray-500">
              Alle Preise verstehen sich zzgl. gesetzlicher MwSt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Pricing */}
      <section className="pb-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-gray-900 to-black text-white rounded-3xl overflow-hidden shadow-2xl"
          >
            <div className="grid md:grid-cols-2">
              {/* Setup */}
              <div className="p-12 border-r border-white/10">
                <div className="text-sm text-gray-400 mb-2">Einmalig</div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-7xl">500</span>
                  <span className="text-3xl text-gray-400">€</span>
                </div>
                <div className="text-xl mb-8">Setup & Erstellung</div>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Professionelle Website-Erstellung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Individuelles Design</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Geschützte Entwurfsansicht vor Bestellung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Domain-Einrichtung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Launch-Support</span>
                  </li>
                </ul>
              </div>

              {/* Monthly */}
              <div className="p-12 bg-white/5">
                <div className="text-sm text-gray-400 mb-2">Monatlich</div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-7xl">99</span>
                  <span className="text-3xl text-gray-400">€</span>
                </div>
                <div className="text-xl mb-8">Betrieb & Service</div>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Premium-Hosting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Wartung & Updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Sicherheit & Backups</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>DSGVO-Betreuung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Support inklusive</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="p-12 bg-white/10 backdrop-blur-sm">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
                <div>
                  <div className="text-2xl font-semibold mb-2">
                    Gesamtkosten im ersten Jahr
                  </div>
                  <div className="text-gray-300">
                    500 € Setup + 12 × 99 € = 1.688 € netto zzgl. gesetzlicher MwSt.
                  </div>
                </div>
                <Link
                  to="/fragebogen"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 group whitespace-nowrap"
                >
                  Jetzt starten
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-300">
                * Alle Preise verstehen sich zzgl. gesetzlicher MwSt. Für Neukunden
                gilt eine Mindestlaufzeit von 12 Monaten.
              </p>
            </div>
          </motion.div>

          {/* What's Included */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-semibold mb-8 text-center">
              Inklusive 
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {included.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 p-4 bg-gray-50 rounded-lg"
                >
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Plus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-4xl lg:text-5xl mb-6">Optionale Add-ons</h2>
            <p className="text-xl text-gray-600">
              Erweitern Sie Ihre Website nach Bedarf
            </p>
            <p className="mt-4 text-sm font-medium text-gray-500">
              Alle Add-on-Preise verstehen sich zzgl. gesetzlicher MwSt.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-[repeat(16,minmax(0,1fr))] gap-6">
            {addons.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className={[
                  "bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300",
                  "lg:col-span-4",
                  index === 0 ? "lg:col-start-3" : "",
                  index === 3 ? "lg:col-start-5" : "",
                ].join(" ")}
              >
                <h3 className="text-lg font-semibold mb-2">{addon.name}</h3>
                <div className="text-2xl font-semibold text-gray-900 mb-3">
                  {addon.price}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {addon.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Häufige Fragen</h2>
          </motion.div>
          <div className="space-y-4">
            {faq.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-2">{item.question}</h3>
                <p className="text-gray-600 leading-relaxed">{item.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl mb-6">
              Haben unsere Preise überzeugt?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Starten Sie jetzt mit Ihrer professionellen Website
            </p>
            <Link
              to="/fragebogen"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 group"
            >
              Jetzt starten
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
